// Features: M004-F0807 (WebUI #270, slice M2a: the read-only "Update available" list)
// Tests: T-M004-0467 (see CalendarUpdateList.test.js beside this file).
//
// Read-only. proposal.list with the state, sort, customer and cursor filters. Hidden, with NO request, unless the view gate is on
// AND the user can manage the Ledger. Refusals go through refusalSentence; the server's own message is never rendered.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { calendarUpdateApi } from '../../../services/calendarUpdateApi';
import { CALENDAR_UPDATE_VIEW_ENABLED, refusalCode, refusalSentence } from './calendarUpdateModel';
import { formatDate } from '../statementModel';

const PAGE_SIZE = 20;
const STATES = [
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'declined', label: 'Declined' },
  { value: 'any', label: 'All' },
];
const SORTS = [
  { value: 'occurrence_date', label: 'Occurrence date' },
  { value: 'created_at', label: 'Created' },
];

const buildParams = ({ state, sort, customerId, cursor }) => ({
  state,
  sort,
  direction: 'asc',
  limit: PAGE_SIZE,
  ...(customerId === undefined ? {} : { customer_user_id: customerId }),
  ...(cursor ? { cursor } : {}),
});

export default function CalendarUpdateList({
  orgId, canManage, onOpen, viewEnabled = CALENDAR_UPDATE_VIEW_ENABLED, api = calendarUpdateApi,
}) {
  const active = Boolean(viewEnabled && canManage);
  const [state, setState] = useState('pending');
  const [sort, setSort] = useState('occurrence_date');
  const [customer, setCustomer] = useState('');
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestSeq = useRef(0);
  const lastRequest = useRef(null);

  const customerId = /^\d+$/.test(customer) ? Number(customer) : undefined;

  const fetchPage = useCallback((params, append) => {
    const seq = ++requestSeq.current;
    lastRequest.current = { params, append };
    setLoading(true);
    setError(null);
    api.list(orgId, params).then((res) => {
      if (seq !== requestSeq.current) return;
      const data = res?.data?.data || {};
      const proposals = data.proposals || [];
      setRows((previous) => (append ? [...previous, ...proposals] : proposals));
      setCursor(data.next_cursor || null);
      setLoading(false);
    }).catch((failure) => {
      if (seq !== requestSeq.current) return;
      setError({ detail: refusalCode(failure) ? refusalSentence(failure) : '' });
      setLoading(false);
    });
  }, [api, orgId]);

  useEffect(() => {
    if (!active) return;
    setRows([]);
    setCursor(null);
    fetchPage(buildParams({ state, sort, customerId }), false);
  }, [active, fetchPage, state, sort, customerId]);

  if (!active) return null;

  const retry = () => {
    const last = lastRequest.current;
    if (last) fetchPage(last.params, last.append);
  };
  const loadMore = () => fetchPage(buildParams({ state, sort, customerId, cursor }), true);

  return (
    <section className="calendar-update-list" aria-label="Calendar updates">
      <div className="calendar-update-list__filters">
        <label>
          State
          <select value={state} onChange={(event) => setState(event.target.value)}>
            {STATES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label>
          Sort by
          <select value={sort} onChange={(event) => setSort(event.target.value)}>
            {SORTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
        <label>
          Customer ID
          <input inputMode="numeric" value={customer} onChange={(event) => setCustomer(event.target.value.trim())} />
        </label>
      </div>

      {error && (
        <div role="alert" className="calendar-update-list__error">
          <p>Calendar updates could not be loaded. Try again.</p>
          {error.detail && <p>{error.detail}</p>}
          <button type="button" onClick={retry}>Try again</button>
        </div>
      )}

      {!error && !loading && rows.length === 0 && <p>No calendar updates are waiting.</p>}

      <ul className="calendar-update-list__rows">
        {rows.map((proposal) => (
          <li key={proposal.proposal_id}>
            <button type="button" onClick={() => onOpen && onOpen(proposal)}>
              <span>{proposal.customer_display_name}</span>
              <span>{proposal.booking_label}</span>
              <span>{formatDate(proposal.occurrence_date)}</span>
              {state !== 'pending' && <span className="calendar-update-list__state">{proposal.state}</span>}
            </button>
          </li>
        ))}
      </ul>

      {loading && <p>Loading...</p>}
      {!loading && cursor && <button type="button" onClick={loadMore}>Load more</button>}
    </section>
  );
}
